/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Icon, Tooltip } from '@components/atoms'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import {
	useAppDispatch,
	useAppSelector,
	EditMenuType,
	adminActions,
	AdminStateType,
} from '@redux/index'

interface PropsType {
	readOnly?: boolean
}

/** 경로 — 밑줄 한 줄(클릭하면 상위 폴더 선택). 아래에 uri 입력(라우트, 직접 고친다) */
export const AdminEditMenuPath = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const editTempMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)
	const editMenuMode: AdminStateType['editMenuMode'] = useAppSelector(
		state => state.admin.editMenuMode,
	)

	// variables
	const readOnly: boolean = props.readOnly ?? false
	const disabled: boolean = editMenuMode === 'update' && editTempMenu.id === ''

	/** uri 는 사람이 정한다 — 경로에서 자동으로 만든 값을 덮어쓰지 않는다(Path.nextUri, PENDING §82) */
	const onUriChange = (value: string) => {
		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...editTempMenu, uri: value.trim() }))
	}

	const onOpenModal = () => {
		reduxDispatch(adminActions.MENU_OPEN_MODAL('menuTree'))
	}

	return (
		<View css={style}>
			<View
				className={`pathView ${disabled ? 'disabled' : ''} ${readOnly ? 'readOnly' : ''}`}
			>
				<span className="fieldLabel">경로</span>
				<Tooltip title="경로 변경을 원하시면 클릭하세요.">
					<Pressable
						className="pathButton"
						onClick={onOpenModal}
						disabled={disabled || readOnly}
					>
						<View className="pathButtonInnerView">
							<Icon name="folder-outline" className="pathFolderIcon" />
							<span className="pathText">{editTempMenu.path.replace('root/', '/')}</span>
						</View>
						<Icon name="chevron-right" className="pathChevron" />
					</Pressable>
				</Tooltip>
			</View>

			<View className="uriView">
				<span className="fieldLabel">uri</span>
				<input
					className="boInput uriInput"
					type="text"
					value={editTempMenu.uri}
					placeholder="/main/order"
					spellCheck={false}
					disabled={disabled || readOnly}
					onChange={e => onUriChange(e.target.value)}
					aria-label="uri"
				/>
				<span className="uriHint">
					라우트 주소다. 경로·이름을 바꿔도 이 값은 그대로 두고, 비어 있을 때만 경로에서
					만든다.
				</span>
			</View>
		</View>
	)
}

const style = css({
	marginBottom: 8,
	fontFamily: boFont.ui,

	'.fieldLabel': {
		display: 'block',
		fontSize: 11,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		fontWeight: 500,
		color: mono.faint,
	},

	'.pathView': {
		position: 'relative',
		marginTop: 20,

		'.pathButton': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			width: '100%',
			height: 40,
			padding: 0,
			borderBottom: `1px solid ${mono.lineInput}`,
			borderRadius: 0,
			transition: 'border-color 200ms ease-out',

			'&:hover:not(:disabled)': { borderBottomColor: mono.ink },
			'&:disabled': { opacity: 0.5, borderBottomStyle: 'dotted' },

			'.pathButtonInnerView': {
				display: 'flex',
				alignItems: 'center',
				gap: 8,
				minWidth: 0,

				'.pathFolderIcon': { fontSize: 18, color: mono.faint },

				'.pathText': {
					fontSize: 14,
					fontWeight: 300,
					color: mono.ink,
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				},
			},

			'.pathChevron': { fontSize: 16, color: mono.faint },
		},

		'&.readOnly .pathButton': { borderBottomStyle: 'dotted' },
	},

	'.uriView': {
		marginTop: 20,

		'.uriInput': {
			width: '100%',
			height: 40,
			padding: 0,
			border: 0,
			borderBottom: `1px solid ${mono.lineInput}`,
			borderRadius: 0,
			background: 'transparent',
			fontFamily: boFont.mono,
			fontSize: 13,
			fontWeight: 400,
			color: mono.ink,
			outline: 'none',
			transition: 'border-color 200ms ease-out',
			'&:focus': { borderBottomColor: mono.ink },
			'&:disabled': { opacity: 0.5, borderBottomStyle: 'dotted' },
			'&::placeholder': { color: mono.faint },
		},
		'.uriHint': {
			display: 'block',
			marginTop: 8,
			fontSize: 12,
			fontWeight: 300,
			lineHeight: 1.5,
			color: mono.faint,
		},
	},
})
